import { createElement } from 'react';
import { Label } from '../../Label';
import { Flow } from '../../Layouts/Flow';
import { ColorIndicatorProps } from '.';
import { postfixTestId } from '../../../util/testId';
import { useNCTextLocalizer } from '../../../locale/useNCTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../../theme';
import { useCustomColorPickerA11yInfo } from './hooks/useCustomColorPickerA11yInfo';
import cx from 'classnames';
import cn from './ColorIndicator.css.json';
import './ColorIndicator.css';

export const ColorIndicator = (props: ColorIndicatorProps) => {
  const localize = useNCTextLocalizer('ColorPicker');
  const { currentColor, newColor } = props;

  const getCustomColorPickerA11yInfo = useCustomColorPickerA11yInfo();
  const {
    currentAriaLabel,
    currentToolTip,
    newAriaLabel,
    newToolTip,
  } = getCustomColorPickerA11yInfo(
    currentColor.color,
    currentColor.type!,
    newColor.color,
    newColor.type!
  );

  const dt = useDesignTokens();
  const colorIndicatorTokens = renderInlineCssVars(
    {
      'spacing-xs': dt.spacing.core.xs,
      'no-color-mark': dt.c.colorAlert,
      'swatch-bd': dt.c.colorUiBorder,
      'automatic-swatch-width': '1.5rem',
    },
    'color-indicator'
  );

  const currentSwatchBorder =
    (currentColor.color === '#FFFFFF' && dt.themeVariant === 'light') ||
    (currentColor.color === '#000000' && dt.themeVariant === 'high-contrast')
      ? cn.swatchBorder
      : undefined;

  const newSwatchBorder =
    (newColor.color === '#FFFFFF' && dt.themeVariant === 'light') ||
    (newColor.color === '#000000' && dt.themeVariant === 'high-contrast')
      ? cn.swatchBorder
      : undefined;

  const MultiColorSwatchComponent = props.renderAutomaticSwatch
    ? props.renderAutomaticSwatch({
        getAttributes() {
          return {
            // eslint-disable-next-line @sas/i18n/jsx-to-localize
            ariaLabel: `${localize('currentColorAutomatic.label')} ${localize(
              'automatic.label'
            )} (${localize('multipleSmallCaseColors.label')})`,

            // eslint-disable-next-line @sas/i18n/jsx-to-localize
            title: `${localize('currentColorAutomatic.label')} ${localize(
              'automatic.label'
            )} (${localize('multipleSmallCaseColors.label')})`,
            className: cn.currentMultiColor,
            tabIndex: 0,
            role: 'img',
          };
        },
      })
    : null;

  return (
    <Flow
      data-testid={postfixTestId(props.testId, 'currentNewSwatchContainer')}
      className={cn.currentNewSwatchContainer}
      spacing="none"
      style={colorIndicatorTokens}
    >
      <Label testId={postfixTestId(props.testId, 'currentLabel')}>
        {localize('customTab.currentColorSwatch.label')}
      </Label>
      {currentColor.type === 'automatic' && props.renderAutomaticSwatch ? (
        MultiColorSwatchComponent
      ) : (
        <Flow
          role="img"
          spacing="none"
          tabIndex={0}
          className={cx(
            cn.swatch,
            cn.swatchSpacing,
            cn.currentColorSwatchBorder,
            currentColor.color === '' && cn.noColor,
            currentSwatchBorder
          )}
          data-testid={postfixTestId(props.testId, 'currentColorSwatch')}
          title={currentToolTip}
          aria-label={currentAriaLabel}
          style={{
            backgroundColor: currentColor.color,
          }}
        />
      )}

      <Flow
        role="img"
        spacing="none"
        tabIndex={0}
        className={cx(
          cn.swatch,
          cn.newColorSwatchBorder,
          newColor.color === '' && cn.noColor,
          newSwatchBorder
        )}
        data-testid={postfixTestId(props.testId, 'newColorSwatch')}
        title={newToolTip}
        aria-label={newAriaLabel}
        style={{ backgroundColor: newColor.color }}
      />
      <Label testId={postfixTestId(props.testId, 'newLabel')}>
        {localize('customTab.newColorSwatch.label')}
      </Label>
    </Flow>
  );
};
